import { useCallback, useMemo } from 'react';
import { useSearchParams, NavigateOptions } from 'react-router-dom';

type Primitive = string | number | boolean;
type StateValue = Primitive | Primitive[];

function serializeValue(value: StateValue): string {
  if (Array.isArray(value)) {
    return value.join(',');
  }
  return String(value);
}

function parseValue(value: string, defaultValue: StateValue): StateValue {
  if (value === '') return defaultValue;

  if (Array.isArray(defaultValue)) {
    const items = value.split(',').map((v) => v.trim());

    const allAreNumbers = items.every((v) => !isNaN(Number(v)));

    return allAreNumbers ? items.map(Number) : items;
  }

  switch (typeof defaultValue) {
    case 'number':
      return Number(value);
    case 'boolean':
      return value === 'true';
    default:
      return value;
  }
}

type State = Record<string, StateValue>;

export function useQueryParamState<T extends State>(
  defaultState: T,
): [
  T,
  (newState: Partial<T>, options?: NavigateOptions) => void,
  (keys: string[], options?: NavigateOptions) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => {
    return Object.entries(defaultState).reduce<State>(
      (acc, [key, defaultValue]) => {
        const paramValue = searchParams.get(key);
        if (paramValue !== null) {
          acc[key] = parseValue(paramValue, defaultValue);
        }
        return acc;
      },
      { ...defaultState },
    ) as T;
  }, [searchParams]);

  const setState = useCallback(
    (newState: Partial<T>, options?: NavigateOptions) => {
      setSearchParams(
        (prevParams) => {
          const updatedState = { ...state, ...newState };
          const params = new URLSearchParams(prevParams);
          Object.entries(updatedState).forEach(([key, value]) => {
            const serializedValue = serializeValue(value);
            if (serializedValue !== serializeValue(defaultState[key])) {
              params.set(key, serializedValue);
            } else {
              params.delete(key);
            }
          });
          return params;
        },
        { replace: true, ...options },
      );
    },
    [state, defaultState, setSearchParams],
  );

  const resetState = useCallback(
    (keys: string[], options?: NavigateOptions) => {
      const newState = keys.reduce<State>((acc, key) => {
        acc[key] = defaultState[key];
        return acc;
      }, {});

      setState(newState as Partial<T>, options);
    },
    [setState],
  );

  return [state, setState, resetState];
}
